// Barra lateral: árvore de páginas (instantânea, a partir da árvore local),
// menus de cada página, arrastar para mover/reordenar, renomear no lugar,
// redimensionar e recolher.

import * as store from "./store.js";
import * as router from "./router.js";
import { el, icon, titleOf, menu, safeGet, safeSet, toast, confirmDialog } from "./dom.js";
import { quickSearch, moveDialog, trashDialog, newDatabaseName, sendDialog, reminderDialog, versionsDialog, shareDialog, copyPageLink } from "./dialogs.js";
import { ui, iconNode } from "./icons.js";
import { avatar } from "./people.js";

const expanded = new Set(safeGet("ordo.expanded", []));
const collapsedSections = new Set(safeGet("ordo.collapsedSections", []));
let host = null;
let renaming = null;
let drag = null;
let scheduled = false;

const saveExpanded = () => safeSet("ordo.expanded", [...expanded]);

export function mount(container) {
  host = container;
  store.on("tree", schedule);
  setupResize();
  render();
}

export function schedule() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    render();
  });
}

// Abre os ancestrais da página aberta para ela aparecer na árvore.
export function reveal(id) {
  let changed = false;
  for (const a of store.ancestors(id)) {
    if (!expanded.has(a.id)) {
      expanded.add(a.id);
      changed = true;
    }
  }
  if (changed) saveExpanded();
  schedule();
  requestAnimationFrame(() => requestAnimationFrame(() => {
    host.querySelector(`.tree-row[data-id="${id}"]`)?.scrollIntoView({ block: "nearest" });
  }));
}

function activeId() {
  const r = router.current();
  if (r.view !== "page") return null;
  const n = store.node(r.id);
  if (n) return r.id;
  // Linha aberta: destaca o banco de dados.
  return store.cached(r.id)?.page?.parent_id || null;
}

function render() {
  if (!host || !store.state.me) return;
  const scrollTop = host.querySelector(".side-scroll")?.scrollTop || 0;
  // O campo de renomear é reaproveitado e muda de lugar ao montar a árvore,
  // o que tira o foco dele: guarda o estado para devolver no fim.
  const typing = renameEl && document.activeElement === renameEl
    ? { start: renameEl.selectionStart, end: renameEl.selectionEnd }
    : null;
  rerendering = true;
  const { mine, shared, spaces } = store.roots();
  const active = activeId();
  const r = router.current();
  const me = store.state.me;
  const head = el("div", { class: "side-head" },
    el("button", { class: "side-user", title: "Seu perfil e configurações", onclick: () => router.go({ view: "settings" }) },
      avatar(me),
      el("span", { class: "side-user-name" }, me.name || "Ordo"),
      me.role === "admin" ? el("span", { class: "side-badge", title: "Administrador do Ordo" }, "admin") : null),
    el("button", { class: "icon-btn side-collapse", title: "Recolher barra lateral (Ctrl+\\)", onclick: toggleCollapsed }, ui("collapse")));
  const unread = store.state.unread;
  const nav = el("div", { class: "side-nav" },
    navItem("search", "Buscar", "Ctrl+K", () => quickSearch()),
    navItem("home", "Início", null, () => router.go({ view: "home" }), r.view === "home"),
    navItem("inbox", "Caixa de entrada", unread ? el("span", { class: "nav-count" }, String(unread > 99 ? "99+" : unread)) : null, () => router.go({ view: "inbox" }), r.view === "inbox"),
    navItem("tasks", "Minhas tarefas", null, () => router.go({ view: "tasks" }), r.view === "tasks"),
    navItem("ai", "Perguntar ao Ordo", null, () => router.go({ view: "ai" }), r.view === "ai"),
    navItem("org", "Equipe", null, () => router.go({ view: "team" }), r.view === "team"),
    navItem("plus", "Nova página", "Ctrl+N", () => newPage()));
  const favs = store.state.favorites.map((fid) => store.node(fid)).filter(Boolean);
  const tree = el("div", { class: "side-scroll" },
    favs.length ? section("Favoritos", favs, null, { key: "fav" }) : null,
    ...orderedSpaces().map((w) => section(w.name, spaces.get(w.id) || [], store.canEditWorkspace(w) ? () => newPage(null, { workspace_id: w.id }) : null,
      { key: "ws:" + w.id, workspace: w })),
    section("Particular", mine, () => newPage(), { key: "private", private: true }),
    shared.length ? section("Compartilhadas comigo", shared, null, { key: "shared" }) : null);
  const foot = el("div", { class: "side-foot" },
    me.role === "admin" ? navItem("space", "Novo ambiente", null, async () => (await import("./spaces.js")).createWorkspaceDialog()) : null,
    navItem("import", "Importar do Notion", null, async () => (await import("./importer.js")).importDialog()),
    navItem("trash", "Lixeira", null, () => trashDialog()),
    me.role === "admin" ? navItem("admin", "Administração", null, () => router.go({ view: "admin" }), r.view === "admin") : null,
    navItem("settings", "Configurações", null, () => router.go({ view: "settings" }), r.view === "settings"));
  host.replaceChildren(head, nav, tree, foot, el("div", { class: "side-resize", title: "Arraste para redimensionar" }));
  if (typing && renameEl?.isConnected) {
    renameEl.focus();
    renameEl.setSelectionRange(typing.start, typing.end);
  }
  rerendering = false;
  host.querySelector(".side-scroll").scrollTop = scrollTop;

  function section(label, items, onAdd, opts = {}) {
    const closed = collapsedSections.has(opts.key);
    const w = opts.workspace;
    const sec = el("div", { class: "side-section" + (w ? " is-space" : "") },
      el("div", { class: "side-section-head", onclick: (e) => {
        if (e.target.closest("button")) return;
        if (collapsedSections.has(opts.key)) collapsedSections.delete(opts.key); else collapsedSections.add(opts.key);
        safeSet("ordo.collapsedSections", [...collapsedSections]);
        schedule();
      } },
        w ? el("span", { class: "side-space-icon" }, iconNode(w.icon, "🗂️")) : null,
        el("span", { class: "side-section-label" }, label),
        closed ? el("span", { class: "muted small" }, " ›") : null,
        el("span", { class: "grow" }),
        w ? el("button", { class: "icon-btn tiny", title: "Membros e configurações do ambiente", onclick: (e) => spaceMenu(w, e.currentTarget) }, ui("more", 14)) : null,
        onAdd ? el("button", { class: "icon-btn tiny", title: "Nova página", onclick: onAdd }, ui("plus", 14)) : null),
      closed ? null : items.length
        ? items.map((p) => item(p, 0, active))
        : el("p", { class: "side-empty" }, onAdd ? "Nenhuma página. Clique em + para criar." : "Nada aqui ainda."));
    if (onAdd && !closed) {
      // Soltar no espaço vazio da seção: vai para o topo dela (e para o ambiente dela).
      sec.addEventListener("dragover", (e) => { if (drag && (e.target === sec || e.target.closest(".side-section-head"))) e.preventDefault(); });
      sec.addEventListener("drop", (e) => {
        if (!drag || !(e.target === sec || e.target.closest(".side-section-head"))) return;
        e.preventDefault();
        movePage(drag.id, null, null, w ? w.id : null);
      });
    }
    return sec;
  }
}

// Ambiente principal (escolhido por cada pessoa) aparece primeiro.
function orderedSpaces() {
  const main = safeGet("ordo.mainSpace", null);
  const list = [...store.state.workspaces];
  const i = list.findIndex((w) => w.id === main);
  if (i > 0) list.unshift(...list.splice(i, 1));
  return list;
}

async function spaceMenu(w, anchor) {
  const { workspaceDialog } = await import("./spaces.js");
  const isMain = orderedSpaces()[0]?.id === w.id;
  menu(anchor, [
    !isMain && { label: "Tornar o ambiente principal", icon: ui("star", 14), run: () => { safeSet("ordo.mainSpace", w.id); schedule(); } },
    { label: "Membros e acesso", icon: ui("users", 14), run: () => workspaceDialog(w.id, "members") },
    w.role === "admin" && { label: "Nome, ícone e descrição", icon: ui("settings", 14), run: () => workspaceDialog(w.id, "general") },
    store.canEditWorkspace(w) && { label: "Nova página", icon: ui("plus", 14), run: () => newPage(null, { workspace_id: w.id }) },
    store.canEditWorkspace(w) && { label: "Novo banco de dados", icon: ui("database", 14), run: () => newPage(null, { workspace_id: w.id, kind: "database", title: newDatabaseName() }) },
    { label: "Perguntar à IA sobre este ambiente", icon: ui("ai", 14), run: () => router.go({ view: "ai", workspace: w.id }) },
  ], { align: "left" });
}

function navItem(ic, label, hint, run, active = false) {
  return el("button", { class: "nav-item" + (active ? " active" : ""), onclick: run },
    el("span", { class: "nav-icon" }, ui(ic)), el("span", { class: "nav-label" }, label),
    hint ? (typeof hint === "string" ? el("span", { class: "nav-hint" }, hint) : hint) : null);
}

function item(p, depth, active) {
  const kids = p.kind === "database" ? [] : store.children(p.id);
  const open = expanded.has(p.id);
  const canEdit = store.canEdit(p);
  const row = el("div", {
    class: "tree-row" + (p.id === active ? " active" : ""),
    dataset: { id: p.id },
    style: { paddingLeft: 6 + depth * 14 + "px" },
    draggable: canEdit ? "true" : null,
    onclick: (e) => {
      if (e.target.closest("button,input")) return;
      router.openPage(p.id);
      closeOnMobile();
    },
    onauxclick: (e) => { if (e.button === 1) e.preventDefault(); },
    oncontextmenu: (e) => { e.preventDefault(); pageMenu(p, { x: e.clientX, y: e.clientY }); },
  });
  const caret = el("button", {
    class: "caret" + (open ? " open" : "") + (p.kind === "database" ? " hidden" : ""),
    title: open ? "Recolher" : "Expandir",
    onclick: () => {
      if (expanded.has(p.id)) expanded.delete(p.id); else expanded.add(p.id);
      saveExpanded();
      schedule();
    },
  }, el("span", {}, "›"));
  const iconBox = el("span", { class: "tree-icon" }, icon(p));
  const title = renaming === p.id
    ? renameInput(p)
    : el("span", { class: "tree-title" }, titleOf(p));
  const actions = el("span", { class: "tree-actions" },
    el("button", { class: "icon-btn tiny", title: "Excluir, renomear, mover…", onclick: (e) => pageMenu(p, e.currentTarget) }, ui("more", 14)),
    p.kind !== "database" && canEdit
      ? el("button", { class: "icon-btn tiny", title: "Adicionar uma página dentro", onclick: () => newPage(p.id) }, ui("plus", 14))
      : null);
  row.append(caret, iconBox, title, actions);
  if (canEdit) setupDrag(row, p);
  const box = el("div", { class: "tree-item" }, row);
  if (open && p.kind !== "database") {
    box.append(kids.length
      ? el("div", { class: "tree-children" }, kids.map((c) => item(c, depth + 1, active)))
      : el("div", { class: "tree-none", style: { paddingLeft: 30 + depth * 14 + "px" } }, "Nenhuma página dentro"));
  }
  return box;
}

let renameEl = null;
let rerendering = false;

function renameInput(p) {
  // Mantém o mesmo campo entre redesenhos da árvore (não perde o que foi digitado).
  if (renameEl && renameEl.dataset.id === p.id) return renameEl;
  const input = el("input", { class: "tree-rename", value: p.title || "", dataset: { id: p.id } });
  renameEl = input;
  const done = (save) => {
    if (renaming !== p.id) return;
    renaming = null;
    renameEl = null;
    const v = input.value.trim();
    if (save && v !== p.title) store.patch(p.id, { title: v }).catch(() => {});
    schedule();
  };
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") done(true);
    if (e.key === "Escape") done(false);
    e.stopPropagation();
  });
  // Redesenhar a árvore tira o campo da tela por um instante: só conta como
  // "terminou de renomear" se o foco realmente foi para outro lugar.
  input.addEventListener("blur", () => setTimeout(() => {
    if (rerendering || document.activeElement === input) return;
    done(true);
  }, 0));
  requestAnimationFrame(() => { input.focus(); input.select(); });
  return input;
}

// Menu "⋯" de uma página (barra lateral e barra superior). `view` (a página
// aberta) habilita as opções de aparência.
export function pageMenu(p, anchor, view = null) {
  const canEdit = store.canEdit(p);
  const fav = store.isFavorite(p.id);
  const format = view?.data?.page.format || {};
  const setFormat = (patch) => view?.setFormat(patch);
  menu(anchor, [
    { label: fav ? "Remover dos favoritos" : "Adicionar aos favoritos", icon: ui(fav ? "unstar" : "star", 14), run: () => store.toggleFavorite(p.id) },
    { label: "Compartilhar…", icon: ui("users", 14), run: () => shareDialog(p.id, anchor) },
    { label: "Copiar link", icon: ui("link", 14), run: () => copyPageLink(p.id) },
    canEdit && { label: "Renomear", icon: ui("type", 14), run: () => { renaming = p.id; reveal(p.id); schedule(); } },
    canEdit && p.kind !== "database" && { label: "Nova página dentro", icon: ui("plus", 14), run: () => newPage(p.id) },
    canEdit && p.kind !== "database" && { label: "Novo banco de dados dentro", icon: ui("database", 14), run: () => newDatabase(p.id) },
    { label: "Duplicar", icon: ui("copy", 14), run: () => duplicatePage(p) },
    canEdit && { label: "Mover para…", icon: ui("move", 14), run: () => moveDialog(p.id) },
    canEdit && store.state.me?.role === "admin" && p.kind !== "row" && { label: "Transformar em ambiente…", icon: ui("users", 14), run: () => toWorkspace(p) },
    { label: "Enviar para…", icon: ui("send", 14), run: () => sendDialog(p.id) },
    { label: "Lembrar-me…", icon: ui("bell", 14), run: () => reminderDialog(p.id) },
    "-",
    p.kind !== "database" && { label: "Transformar em tarefas (IA)", icon: ui("ai", 14), run: async () => (await import("./ai-ui.js")).extractTasksDialog(p.id) },
    p.kind !== "database" && { label: "Histórico de versões", icon: ui("history", 14), run: () => versionsDialog(p.id, () => view?.reload()) },
    view && canEdit && p.kind !== "database" && "-",
    view && store.canEdit(p) && p.kind !== "database" && { label: `Largura total${format.fullWidth ? "  ✓" : ""}`, icon: ui("wide", 14), run: () => setFormat({ fullWidth: !format.fullWidth }) },
    view && store.canEdit(p) && p.kind !== "database" && { label: `Texto pequeno${format.smallText ? "  ✓" : ""}`, icon: ui("type", 14), run: () => setFormat({ smallText: !format.smallText }) },
    view && store.canEdit(p) && p.kind !== "database" && { label: `Fonte: ${format.font === "serif" ? "serifada" : format.font === "mono" ? "mono" : "padrão"}`, icon: "Aa",
      run: () => setFormat({ font: format.font === "serif" ? "mono" : format.font === "mono" ? null : "serif" }) },
    view && store.canEdit(p) && { label: format.locked ? "Destravar página" : "Travar página", icon: ui(format.locked ? "unlock" : "lock", 14), run: () => setFormat({ locked: !format.locked }) },
    canEdit && "-",
    canEdit && { label: "Excluir", icon: ui("trash", 14), danger: true, run: () => removePage(p) },
  ], { align: "left" });
}

// A página (com tudo que tem dentro) vira um ambiente próprio, para dar acesso
// só a ela: quem entra no ambiente novo não vê o resto.
async function toWorkspace(p) {
  const ok = await confirmDialog(`Transformar "${titleOf(p)}" num ambiente próprio? Ela e tudo que tem dentro saem de onde estão e vão para o ambiente novo. Depois é só adicionar as pessoas em "Membros e acesso": elas vão ver só este ambiente. Relações e automações com outros bancos continuam funcionando.`, { ok: "Transformar em ambiente" });
  if (!ok) return;
  try {
    const { api } = await import("./api.js");
    const res = await api("POST", `/pages/${p.id}/to-workspace`, {}, { timeout: 60000 });
    await store.loadTree();
    toast(`Ambiente "${res.workspace.name}" criado. Adicione as pessoas em Membros e acesso.`);
    workspaceDialog(res.workspace.id, "members");
  } catch (err) {
    toast("Não foi possível transformar em ambiente: " + err.message);
  }
}

async function duplicatePage(p) {
  try {
    const { api } = await import("./api.js");
    const res = await api("POST", `/pages/${p.id}/duplicate`, {}, { timeout: 60000 });
    await store.loadTree();
    toast("Cópia criada.");
    if (res.page?.id) router.openPage(res.page.id);
  } catch (err) {
    toast("Não foi possível duplicar: " + err.message);
  }
}

export async function removePage(p) {
  const kids = store.children(p.id).length;
  const what = p.kind === "database" ? "este banco de dados e todas as linhas" : kids ? "esta página e as páginas dentro dela" : "esta página";
  if (!(await confirmDialog(`Mover ${what} para a lixeira? Dá para restaurar depois.`, { ok: "Mover para a lixeira", danger: true }))) return;
  const parent = p.parent_id;
  const r = router.current();
  const openInside = r.view === "page" && (r.id === p.id || store.isInside(r.id, p.id));
  router.forget(p.id);
  await store.archive(p.id).catch(() => {});
  if (openInside) {
    if (parent && store.node(parent)) router.openPage(parent, { replace: true });
    else router.go({ view: "home" }, { replace: true });
  }
  toast("Movido para a lixeira.");
}

export function newPage(parentId = null, extra = {}) {
  const { id } = store.create({ parent_id: parentId, ...extra });
  if (parentId) {
    expanded.add(parentId);
    saveExpanded();
  }
  router.openPage(id);
  closeOnMobile();
  return id;
}

export function newDatabase(parentId = null) {
  return newPage(parentId, { kind: "database", title: newDatabaseName() });
}

// ---------- Arrastar e soltar ----------

function setupDrag(row, p) {
  row.addEventListener("dragstart", (e) => {
    drag = { id: p.id };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/ordo-page", p.id);
    row.classList.add("dragging");
  });
  row.addEventListener("dragend", () => {
    drag = null;
    row.classList.remove("dragging");
    clearDropMarks();
  });
  row.addEventListener("dragover", (e) => {
    if (!drag || drag.id === p.id || store.isInside(p.id, drag.id)) return;
    e.preventDefault();
    const zone = dropZone(row, e, p);
    clearDropMarks();
    row.classList.add("drop-" + zone);
  });
  row.addEventListener("dragleave", () => row.classList.remove("drop-before", "drop-after", "drop-inside"));
  row.addEventListener("drop", (e) => {
    if (!drag || drag.id === p.id) return;
    e.preventDefault();
    const zone = dropZone(row, e, p);
    clearDropMarks();
    if (zone === "inside") {
      expanded.add(p.id);
      saveExpanded();
      movePage(drag.id, p.id, null);
    } else {
      movePage(drag.id, p.parent_id || null, { target: p.id, after: zone === "after" });
    }
  });
}

function dropZone(row, e, p) {
  const r = row.getBoundingClientRect();
  const y = (e.clientY - r.top) / r.height;
  if (p.kind === "database") return y < 0.5 ? "before" : "after";
  if (y < 0.25) return "before";
  if (y > 0.75) return "after";
  return "inside";
}

function clearDropMarks() {
  for (const r of host.querySelectorAll(".drop-before,.drop-after,.drop-inside")) r.classList.remove("drop-before", "drop-after", "drop-inside");
}

async function movePage(id, parentId, place, workspaceId) {
  const page = store.node(id);
  if (!page) return;
  if (parentId === id || (parentId && store.isInside(parentId, id))) return toast("Não dá para mover uma página para dentro dela mesma.");
  const target = parentId ? store.node(parentId) : null;
  if (target?.kind === "database") return toast("Páginas não podem ir para dentro de um banco de dados.");
  const siblings = store.children(parentId).filter((s) => s.id !== id);
  let position;
  if (place) {
    const i = siblings.findIndex((s) => s.id === place.target);
    const before = place.after ? siblings[i] : siblings[i - 1];
    const after = place.after ? siblings[i + 1] : siblings[i];
    if (before && after) position = (before.position + after.position) / 2;
    else if (before) position = before.position + 1000;
    else if (after) position = after.position - 1000;
    else position = Date.now();
  } else {
    position = siblings.length ? siblings.at(-1).position + 1000 : Date.now();
  }
  const body = { position };
  if ((page.parent_id || null) !== (parentId || null)) body.parent_id = parentId;
  if (!parentId && workspaceId !== undefined && (page.workspace_id || null) !== (workspaceId || null)) {
    const target = workspaceId ? store.workspace(workspaceId)?.name : "Particular";
    if (!(await confirmDialog(`Mover "${titleOf(page)}" e tudo dentro dela para ${workspaceId ? `o ambiente "${target}"` : "o seu Particular"}? Quem vê a página passa a ser ${workspaceId ? "quem é membro do ambiente" : "só você"}.`, { ok: "Mover" }))) return;
    body.workspace_id = workspaceId;
  }
  await store.patch(id, body).catch(() => store.loadTree());
}

// ---------- Recolher e redimensionar ----------

export function toggleCollapsed() {
  const app = document.getElementById("app");
  if (innerWidth < 760) {
    app.classList.toggle("side-open");
    return;
  }
  const v = !app.classList.contains("side-collapsed");
  app.classList.toggle("side-collapsed", v);
  safeSet("ordo.sideCollapsed", v);
}

export function closeOnMobile() {
  if (innerWidth < 760) document.getElementById("app").classList.remove("side-open");
}

function setupResize() {
  const app = document.getElementById("app");
  const w = safeGet("ordo.sideWidth", 260);
  app.style.setProperty("--side-w", Math.min(420, Math.max(200, w)) + "px");
  if (safeGet("ordo.sideCollapsed", false)) app.classList.add("side-collapsed");
  host.addEventListener("mousedown", (e) => {
    if (!e.target.classList.contains("side-resize")) return;
    e.preventDefault();
    const move = (ev) => {
      const width = Math.min(420, Math.max(200, ev.clientX));
      app.style.setProperty("--side-w", width + "px");
    };
    const up = (ev) => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.classList.remove("resizing");
      safeSet("ordo.sideWidth", Math.min(420, Math.max(200, ev.clientX)));
    };
    document.body.classList.add("resizing");
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  });
}
